import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { sifreSifirlamaUygula } from '../services/apiService';
import {
  HiLockClosed,
  HiEye,
  HiEyeOff,
  HiCheckCircle,
  HiShieldCheck,
  HiExclamationCircle
} from 'react-icons/hi';
import toast from 'react-hot-toast';
import './PasswordResetPage.css';

const PasswordResetPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError('Geçersiz şifre sıfırlama linki. Token bulunamadı.');
    } else {
      setToken(tokenParam);
    }
  }, [searchParams]);

  const validatePassword = (password) => {
    if (password.length < 6) {
      return 'Şifre en az 6 karakter olmalıdır';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validasyonlar
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      toast.error(passwordError);
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Şifreler eşleşmiyor!');
      return;
    }

    setLoading(true);

    try {
      const response = await sifreSifirlamaUygula(token, newPassword);

      if (response.success) {
        setSuccess(true);
        toast.success('Şifreniz başarıyla güncellendi!');

        // 3 saniye sonra login sayfasına yönlendir
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        toast.error(response.mesaj || 'Şifre güncellenemedi');
        setLoading(false);
      }
    } catch (err) {
      toast.error('Bir hata oluştu. Lütfen tekrar deneyin.');
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div className="password-reset-page">
        <div className="password-reset-container">
          <div className="error-card">
            <div className="error-icon">
              <HiExclamationCircle />
            </div>
            <h2>Geçersiz Link</h2>
            <p>{error}</p>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/login')}
            >
              Giriş Sayfasına Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="password-reset-page">
        <div className="password-reset-container">
          <div className="success-card">
            <div className="success-icon-large">
              <HiCheckCircle />
            </div>
            <h2>Şifre Başarıyla Güncellendi!</h2>
            <p>
              Şifreniz başarıyla değiştirildi. Artık yeni şifrenizle giriş yapabilirsiniz.
            </p>
            <p className="redirect-info">
              3 saniye içinde giriş sayfasına yönlendirileceksiniz...
            </p>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/login')}
            >
              Hemen Giriş Yap
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="password-reset-page">
      <div className="password-reset-container">
        <div className="password-reset-card">
          <div className="card-header">
            <div className="header-icon">
              <HiShieldCheck />
            </div>
            <h2>Yeni Şifre Belirle</h2>
            <p>Hesabınız için yeni bir şifre oluşturun</p>
          </div>

          <form onSubmit={handleSubmit} className="password-reset-form">
            <div className="form-group">
              <label htmlFor="newPassword" className="form-label">
                <HiLockClosed />
                <span>Yeni Şifre</span>
              </label>
              <div className="password-input-wrapper">
                <input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Yeni şifrenizi girin"
                  required
                  autoFocus
                  minLength={6}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label="Şifreyi göster/gizle"
                >
                  {showPassword ? <HiEyeOff /> : <HiEye />}
                </button>
              </div>
              <p className="hint-text">En az 6 karakter olmalıdır</p>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                <HiLockClosed />
                <span>Yeni Şifre (Tekrar)</span>
              </label>
              <div className="password-input-wrapper">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="form-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Şifrenizi tekrar girin"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label="Şifreyi göster/gizle"
                >
                  {showConfirmPassword ? <HiEyeOff /> : <HiEye />}
                </button>
              </div>
            </div>

            <div className="security-note">
              <HiShieldCheck />
              <p>
                Güvenliğiniz için güçlü bir şifre seçin. Şifreniz başka hiçbir yerde kullanmadığınız,
                harf, rakam ve özel karakterler içeren bir kombinasyon olmalıdır.
              </p>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-full"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="spinner-sm"></div>
                  <span>Şifre Güncelleniyor...</span>
                </>
              ) : (
                <>
                  <HiShieldCheck />
                  <span>Şifreyi Güncelle</span>
                </>
              )}
            </button>

            <button
              type="button"
              className="btn btn-secondary btn-full"
              onClick={() => navigate('/login')}
            >
              İptal
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetPage;
